import { IExtension } from '@shell/core/types';
import EmptyProductPage from '@shell/components/EmptyProductPage.vue';
import pluginProductsHelpers from '@shell/core/plugin-products-helpers';
import { BasePluginProduct } from '@shell/core/plugin-products-base';
import { isProductSinglePage } from '@shell/core/plugin-products-type-guards';
import { ProductChild, ProductMetadata, ProductMetadataSinglePage } from '@shell/core/plugin-products-external';
import { AdvancedProductConfigOptionsInternal, ProductMetadataInternal } from '@shell/core/plugin-products-internal';
import { registerExtensionProductRouting } from '@shell/core/plugin-products-route-registry';

/**
 * Represents a new top-level product being added by an extension
 * @internal
 */
export class TopLevelPluginProduct extends BasePluginProduct {
  protected product?: ProductMetadata | ProductMetadataSinglePage;

  get isNewProduct(): boolean {
    return true;
  }

  constructor(plugin: IExtension, product: ProductMetadata | ProductMetadataSinglePage | string, pages: ProductChild[], advancedProdConfig?: AdvancedProductConfigOptionsInternal) {
    super(pages, advancedProdConfig);

    // Convenience/bridge method: create a basic product from just a name string
    if (typeof product === 'string') {
      const emptyProduct: ProductMetadata = {
        name:  product,
        label: product,
      };

      product = emptyProduct;
    }

    // convert this to "string" to match all types moving forward
    // doesn't impact anything, fixes build problems of extensions
    // and allows extensions to use either string literal or enum value for product name
    this.name = this.normalizeProductName(product.name);
    this.product = product;
    this.startRouteWithProduct = (product as ProductMetadataInternal).startRouteWithProduct ?? true;

    // register the product as a top-level product in the plugin object (will be needed for routes correction when on list views for top-level products)
    plugin._registerTopLevelProduct(this.name);
    plugin._setStartRouteWithProduct(this.name, this.startRouteWithProduct);

    // Record how this product routes so that anything extending it later generates matching
    // routes. Must happen before `addRoutes`, which flags the generic resource routes on it.
    registerExtensionProductRouting(this.name, this.startRouteWithProduct);

    this.processConfigChildren();

    // If the product has a `component` field, then this is a single page product
    if (isProductSinglePage(product)) {
      const productSinglePage: ProductMetadataSinglePage = product as ProductMetadataSinglePage;
      // Add the route to vue-router (here we go with the 'plain' layout for simple single page products)
      const route = pluginProductsHelpers.generateTopLevelExtensionSimpleBaseRoute(this.name, { component: productSinglePage.component });

      plugin.addRoute('plain', route);
    } else if (this.config.length === 0) {
      // If no config is provided, add a default empty page
      this.config = [{
        name:      'main',
        label:     'Main',
        component: EmptyProductPage,
      }];

      this.addRoutes(plugin, this.name, this.config);
    } else {
      // This is the general case - product with config items and no single page component
      this.addRoutes(plugin, this.name, this.config);
    }
  }
}
